import { drivelabels_v2 } from '@googleapis/drivelabels';

type Lock = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2LabelLock;

/** The Admin console page for a label, where its rules can be viewed. */
export const ADMIN_CONSOLE_LABEL_URL = 'https://admin.google.com/ac/dc/labels/';

export interface LockIndex {
  /** Any active lock, at any level: the label is used in a Google rule. */
  hasLocks: boolean;
  /** A lock on the whole label: it can't be disabled or deleted. */
  labelLocked: boolean;
  /** A lock on the field itself: it can't be disabled, deleted, given options or reordered. */
  isFieldLocked: (fieldId: string) => boolean;
  /** A lock on one option: it can't be disabled, deleted or moved. */
  isOptionLocked: (fieldId: string, optionId: string) => boolean;
  /** Ids of fields with a lock on the field or on one of its options. */
  lockedFieldIds: Set<string>;
  /**
   * A lock on the field or one of its options: either way the field can't be disabled or deleted,
   * as in the Admin console.
   */
  isFieldOrOptionLocked: (fieldId: string) => boolean;
}

/**
 * Sorts a label's Google rule locks by what they target. Only ACTIVE locks count: a lock without a
 * field targets the label, a field alone targets the field, and a field with a choice targets that
 * option.
 */
export function getLockIndex(locks: Lock[] | undefined): LockIndex {
  const active = (locks ?? []).filter((lock) => lock.state === 'ACTIVE');
  const lockedFields = new Set<string>();
  const lockedOptions = new Set<string>();
  let labelLocked = false;
  for (const lock of active) {
    if (!lock.fieldId) {
      labelLocked = true;
    } else if (!lock.choiceId) {
      lockedFields.add(lock.fieldId);
    } else {
      lockedOptions.add(`${lock.fieldId}/${lock.choiceId}`);
    }
  }
  const lockedFieldIds = new Set(lockedFields);
  for (const lock of active) {
    if (lock.fieldId && lock.choiceId) {
      lockedFieldIds.add(lock.fieldId);
    }
  }
  return {
    hasLocks: active.length > 0,
    labelLocked,
    isFieldLocked: (fieldId) => !!fieldId && lockedFields.has(fieldId),
    isOptionLocked: (fieldId, optionId) =>
      !!fieldId && !!optionId && lockedOptions.has(`${fieldId}/${optionId}`),
    lockedFieldIds,
    isFieldOrOptionLocked: (fieldId) =>
      !!fieldId && lockedFieldIds.has(fieldId),
  };
}

/** No locks: what Create Label and unlocked labels use. */
export const NO_LOCKS: LockIndex = getLockIndex([]);
